import {
  splitPartKey,
  type DraftPart,
} from "@/lib/assetContent";

/**
 * The typed reading of a BG domain document, and the SURGICAL edits an
 * authoring surface makes to one (#859).
 *
 * ## THE ONE RULE THIS MODULE EXISTS FOR
 *
 * "Saving only a prompt never drops hidden conditional fields, quality examples
 * or stable item IDs" is the ticket's own acceptance criterion, and it is a
 * property of HOW an edit is applied rather than of what the editor renders.
 * Base canon's `family_and_childhood` carries eight items, each with an
 * `extraction_note` of several hundred words and up to eight
 * `quality_examples` — none of which #859's surface shows, and #913's
 * conditional/extraction controls are a separate ticket. So every function
 * below reads the stored JSON buffer, replaces exactly the one value that
 * changed IN PLACE, and re-serialises the SAME object. Nothing is rebuilt from
 * a narrowed view, because a narrowed view is precisely a list of the fields
 * that would be dropped.
 *
 * `JSON.parse` preserves insertion order for string keys and in-place mutation
 * does not disturb it, so a save that changes one description re-emits every
 * other key in the order it was stored — which is what keeps a version diff
 * legible and a `quality_examples` block intact.
 *
 * ## WHY THE SCHEMA IS NOT RESTATED HERE
 *
 * `services/editor_service/background_domains.py` is the authority: it refuses
 * an unsupported field, a blank required prompt, a rule naming an item this
 * domain does not have, and a `has_opening_question` that disagrees with the
 * opening text present — each with a field-addressed pointer. A second copy of
 * those rules in the browser would be free to drift from the one that actually
 * refuses, which is the class of duplication `lib/assetContent.ts` documents at
 * length and #300 spent a ticket deleting.
 *
 * What IS here is the projection a surface needs to RENDER: which fields exist,
 * what the opening field is called for this document, and the item list in its
 * stored order. A buffer that cannot be read as a domain document answers
 * `null`, and the surface says so rather than presenting an empty form over
 * content it did not understand.
 *
 * ## STRUCTURE IS EDITED ACROSS EVERY LANGUAGE, TEXT IS NOT
 *
 * A background set stores one `domains` document per language it carries
 * (`he/domains`, `en/domains`). The pinned engine merges them anchored on `he`
 * — `order` and the item list come from the anchor and only display prose is
 * replaced per language — so adding, removing or reordering an item in ONE
 * language would silently produce a document whose English half describes a
 * different interview. Structural edits therefore apply to every document that
 * carries the domain; text edits apply to the one document being typed into.
 */

/** The part role a background set places, matching `placement.BG_DOMAINS_ROLE`. */
export const BG_DOMAINS_ROLE = "domains";

/**
 * The language the pinned engine reads a domain's STRUCTURE from, mirroring
 * `placement.BG_STRUCTURE_LANGUAGE`.
 *
 * Used only to pick which document a surface shows FIRST and which one's
 * `order` is reported as the interview position — never to refuse a write. The
 * service is what enforces that every language declares the same `order`.
 */
export const BG_STRUCTURE_LANGUAGE = "he";

/** The two opening fields, exactly one of which a document may carry. */
export type BackgroundOpeningField = "opening_prompt" | "first_question";

/** One `{value, sufficient, reason}` row shown to the extraction LLM. */
export interface BackgroundQualityExampleView {
  value: string;
  /** The verdict. `null` when the stored value is not a boolean at all. */
  sufficient: boolean | null;
  reason: string;
}

export interface BackgroundItemView {
  /** The stable id every reference resolves against. READ-ONLY on this surface. */
  id: string;
  type: string;
  /** The item's prompt text, in the language of the document it was read from. */
  description: string;
  /**
   * The registration key that pre-populates this item, or `""` when absent.
   *
   * NOT an item reference: it names a key the interview profile registers
   * (`init._prefill_field_for`), which lives in another asset entirely, so
   * nothing in this document could resolve it and no picker here could offer
   * one.
   */
  patientInfoField: string;
  /** Extraction guidance, or `""` when the key is absent. */
  extractionNote: string;
  /** Free prose describing when a conditional item applies, or `""`. */
  trigger: string;
  /**
   * The two gates, as THREE states rather than two.
   *
   * `null` is ABSENT — which is not `false`. The engine reads a missing
   * `binary_gate` and an explicit `binary_gate: false` the same way today, but
   * the document is what a clinician publishes and what an export is compared
   * against, so a control that could only say "false" would rewrite every
   * source document that simply omits the key. `null` is also what a stored
   * value this build cannot read as a boolean projects to; the service refuses
   * that document with a pointer at the field.
   */
  criticalGate: boolean | null;
  binaryGate: boolean | null;
  /**
   * The ordered examples, or `null` when the key is absent.
   *
   * `null` and `[]` are different documents and only one of them is valid: the
   * schema bounds a present list at `min_length=1`, so emptying the list has to
   * remove the key rather than store `[]`.
   */
  qualityExamples: BackgroundQualityExampleView[] | null;
}

/** One deterministic rule: when `when` holds `equals`, trigger and deny. */
export interface BackgroundRuleView {
  when: string;
  equals: string;
  /** `null` when the key is absent; a present list is never empty. */
  valueContains: string[] | null;
  valueNotContains: string[] | null;
  triggerItems: string[] | null;
  denyItems: string[] | null;
}

/** One named safety-net entry. BG completeness logic, never the safety detector. */
export interface BackgroundSafetyNetView {
  /** The entry's name — its identity under `<domain>/<name>` at the tracker. */
  name: string;
  gateItem: string;
  gateStatuses: string[] | null;
  keywords: string[] | null;
  excludeKeywords: string[] | null;
  targetItems: string[] | null;
}

export interface BackgroundDomainView {
  /** The canonical domain id. Stable identity, never edited after create. */
  domain: string;
  displayName: string;
  /** The interview position, or `null` when the stored value is not an integer. */
  order: number | null;
  hasOpeningQuestion: boolean;
  /** Which opening field this document's `has_opening_question` selects. */
  openingField: BackgroundOpeningField;
  /** The text at that field, or `""` when it is absent. */
  openingText: string;
  items: BackgroundItemView[];
  /**
   * The ordered deterministic rules, or `null` when the key is absent.
   *
   * Unlike `qualityExamples` an empty list IS valid here — the schema puts no
   * lower bound on it — but the two states still say different things, and this
   * projection reports which one the document is in rather than collapsing them.
   */
  conditionalTriggerRules: BackgroundRuleView[] | null;
  /**
   * The named safety nets in STORED KEY ORDER, or `null` when the key is absent.
   *
   * A list rather than the map it is stored as: the surface renders rows and
   * `Object.keys` order is the only order a JSON object has, so reading it once
   * here is what keeps the rows stable across a render that changes one field.
   */
  safetyNetTriggers: BackgroundSafetyNetView[] | null;
}

export interface BackgroundDomainDocument {
  /** The `DraftPart.id` this document lives in — the edit address. */
  documentId: string;
  /** The part's language prefix, or `null` for a language-neutral part. */
  language: string | null;
  /** The readable projection, or `null` when the buffer is not a domain document. */
  view: BackgroundDomainView | null;
}

type JsonRecord = Record<string, unknown>;

function isRecord(value: unknown): value is JsonRecord {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readString(source: JsonRecord, key: string): string {
  const value = source[key];
  return typeof value === "string" ? value : "";
}

/**
 * The document behind a draft part's buffer, or `null`.
 *
 * DELIBERATELY LENIENT about everything but the shape a projection needs. A
 * stored version is immutable and re-validated on read, and content authored
 * before #857's write gate existed can hold a field this build does not model —
 * so a missing `display_name` yields an empty string rather than refusing the
 * whole document, and the SERVICE decides on save whether the result may become
 * a version. What genuinely cannot be projected — a buffer that is not JSON, or
 * not an object, or carries no `items` array — answers `null`.
 */
export function readDomainDocument(text: string): BackgroundDomainView | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return null;
  }
  if (!isRecord(parsed) || !Array.isArray(parsed.items)) {
    return null;
  }
  const hasOpeningQuestion = parsed.has_opening_question === true;
  const openingField: BackgroundOpeningField = hasOpeningQuestion
    ? "opening_prompt"
    : "first_question";
  const rules = parsed.conditional_trigger_rules;
  const nets = parsed.safety_net_triggers;

  return {
    domain: readString(parsed, "domain"),
    displayName: readString(parsed, "display_name"),
    // `typeof … === "number"` alone admits `true` in no JSON, but it does admit
    // a float; the engine sorts on this and the service refuses a non-integer,
    // so a value this cannot report as a position is reported as none.
    order: Number.isInteger(parsed.order) ? (parsed.order as number) : null,
    hasOpeningQuestion,
    openingField,
    openingText: readString(parsed, openingField),
    items: parsed.items.map((item, index) => readItem(item, index)),
    conditionalTriggerRules: Array.isArray(rules) ? rules.map(readRule) : null,
    safetyNetTriggers: isRecord(nets)
      ? Object.entries(nets).map(([name, net]) => readSafetyNet(name, net))
      : null,
  };
}

/** A list of strings, or `null` for an absent key. Non-strings are dropped. */
function readStrings(source: JsonRecord, key: string): string[] | null {
  const value = source[key];
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === "string")
    : null;
}

/** A three-state boolean: the value, or `null` for absent-or-unreadable. */
function readFlag(source: JsonRecord, key: string): boolean | null {
  const value = source[key];
  return typeof value === "boolean" ? value : null;
}

function readRule(rule: unknown): BackgroundRuleView {
  if (!isRecord(rule)) {
    // A row that is not an object still occupies a POSITION, for the reason
    // `readItem` states: dropping it renumbers every rule after it on screen
    // while the stored document keeps them.
    return {
      when: "",
      equals: "",
      valueContains: null,
      valueNotContains: null,
      triggerItems: null,
      denyItems: null,
    };
  }
  return {
    when: readString(rule, "when"),
    equals: readString(rule, "equals"),
    valueContains: readStrings(rule, "value_contains"),
    valueNotContains: readStrings(rule, "value_not_contains"),
    triggerItems: readStrings(rule, "trigger_items"),
    denyItems: readStrings(rule, "deny_items"),
  };
}

function readSafetyNet(name: string, net: unknown): BackgroundSafetyNetView {
  if (!isRecord(net)) {
    return {
      name,
      gateItem: "",
      gateStatuses: null,
      keywords: null,
      excludeKeywords: null,
      targetItems: null,
    };
  }
  return {
    name,
    gateItem: readString(net, "gate_item"),
    gateStatuses: readStrings(net, "gate_statuses"),
    keywords: readStrings(net, "keywords"),
    excludeKeywords: readStrings(net, "exclude_keywords"),
    targetItems: readStrings(net, "target_items"),
  };
}

function readItem(item: unknown, index: number): BackgroundItemView {
  if (!isRecord(item)) {
    // A row that is not an object still occupies a POSITION in the ordered
    // list, and dropping it would renumber every item after it on screen while
    // leaving the stored document untouched.
    return {
      id: `#${index + 1}`,
      type: "",
      description: "",
      patientInfoField: "",
      extractionNote: "",
      trigger: "",
      criticalGate: null,
      binaryGate: null,
      qualityExamples: null,
    };
  }
  const examples = item.quality_examples;
  return {
    id: readString(item, "id"),
    type: readString(item, "type"),
    description: readString(item, "description"),
    patientInfoField: readString(item, "patient_info_field"),
    extractionNote: readString(item, "extraction_note"),
    trigger: readString(item, "trigger"),
    criticalGate: readFlag(item, "critical_gate"),
    binaryGate: readFlag(item, "binary_gate"),
    qualityExamples: Array.isArray(examples)
      ? examples.map((example) =>
          isRecord(example)
            ? {
                value: readString(example, "value"),
                sufficient: readFlag(example, "sufficient"),
                reason: readString(example, "reason"),
              }
            : { value: "", sufficient: null, reason: "" },
        )
      : null,
  };
}

/**
 * Every `domains` document this draft carries, in stored part order.
 *
 * The role is matched off the part KEY rather than off the part manifest: this
 * is a projection of what the asset actually holds, and a document stored under
 * a role the current manifest no longer allows must still be shown to the
 * clinician editing it.
 */
export function domainDocuments(
  parts: readonly DraftPart[],
): BackgroundDomainDocument[] {
  const documents: BackgroundDomainDocument[] = [];
  for (const part of parts) {
    const [language, role] = splitPartKey(part.key);
    if (role !== BG_DOMAINS_ROLE || part.kind !== "structured") {
      continue;
    }
    documents.push({
      documentId: part.id,
      language,
      view: readDomainDocument(part.text),
    });
  }
  return documents;
}

/**
 * The document whose STRUCTURE the runtime reads: the `he` one, else the sole
 * one, else `null`.
 *
 * `null` for a set carrying two non-anchor languages, which is a shape the
 * service refuses to place; the surface reports it rather than picking one and
 * presenting its order as the interview position.
 */
export function anchorDocument(
  documents: readonly BackgroundDomainDocument[],
): BackgroundDomainDocument | null {
  const anchored = documents.find(
    (document) => document.language === BG_STRUCTURE_LANGUAGE,
  );
  if (anchored) {
    return anchored;
  }
  return documents.length === 1 ? (documents[0] as BackgroundDomainDocument) : null;
}

/**
 * The parts array with `mutate` applied to one document's parsed object.
 *
 * THE SINGLE WRITE PATH of this module, and the reason every edit below is
 * three lines. It parses, hands the caller the live object, and re-serialises
 * whatever the caller left behind — so a mutation that touches one key
 * necessarily preserves every other one, and no edit can be written in a way
 * that rebuilds the document.
 *
 * `JSON.stringify(…, null, 2)` matches `assetContent.toDraft`'s own
 * pretty-printing, so an edited buffer and a freshly loaded one are the same
 * text when the content is the same — which is what `isSameDraft` compares to
 * decide whether Discard and Save are live.
 *
 * An unparseable buffer is returned UNCHANGED rather than replaced: it is
 * content the clinician has not been shown a form over, and silently
 * overwriting it would lose a stored version's only readable copy.
 */
function editDocument(
  parts: readonly DraftPart[],
  documentId: string,
  mutate: (document: JsonRecord) => void,
): DraftPart[] {
  return parts.map((part) => {
    if (part.id !== documentId) {
      return part;
    }
    let parsed: unknown;
    try {
      parsed = JSON.parse(part.text);
    } catch {
      return part;
    }
    if (!isRecord(parsed)) {
      return part;
    }
    mutate(parsed);
    return { ...part, text: JSON.stringify(parsed, null, 2) };
  });
}

/**
 * The ANCHOR document, edited — or every document when there is no anchor to
 * pick, which is the single-document case `anchorDocument` already treats as
 * anchored.
 *
 * Addressed by ROLE rather than by a `documentId` the caller passes, because
 * "which document does the runtime read this from" is this module's question
 * and not a decision an editing surface should be able to get wrong.
 */
function editAnchorDocument(
  parts: readonly DraftPart[],
  mutate: (document: JsonRecord) => void,
): DraftPart[] {
  const anchor = anchorDocument(domainDocuments(parts));
  return anchor === null ? parts.slice() : editDocument(parts, anchor.documentId, mutate);
}

/** Every `domains` document, edited by the same mutation. */
function editEveryDocument(
  parts: readonly DraftPart[],
  mutate: (document: JsonRecord) => void,
): DraftPart[] {
  let next = parts as DraftPart[];
  for (const document of domainDocuments(parts)) {
    next = editDocument(next, document.documentId, mutate);
  }
  return next;
}

/**
 * The canonical domain id, on EVERY language document.
 *
 * ONE VALUE ACROSS THE VERSION, because the runtime reads a single domain out
 * of them (`_merge_lang_configs`) and `validate_background_domains` checks EACH
 * language's `domain` against the placement coordinate's slug — so a document
 * naming a different one is refused rather than merged.
 *
 * Reached only by the creation dialog, which has to re-identify a copied
 * document to match the coordinate the clinician chose. There is deliberately
 * no control for it on the editing surface: after v1 the id is the key every
 * project pin and every runtime record resolves against.
 */
export function setDomainId(
  parts: readonly DraftPart[],
  domain: string,
): DraftPart[] {
  return editEveryDocument(parts, (document) => {
    document.domain = domain;
  });
}

/** One document-level text field — the display name or the opening. */
export function setDomainText(
  parts: readonly DraftPart[],
  documentId: string,
  field: "display_name" | BackgroundOpeningField,
  value: string,
): DraftPart[] {
  return editDocument(parts, documentId, (document) => {
    document[field] = value;
  });
}

/**
 * The interview position, on EVERY language of the domain.
 *
 * Across all documents deliberately: `background_domains._validate_one_position`
 * refuses a version whose languages declare different orders, because the
 * runtime reads the anchor's and a divergent one is a value nothing will ever
 * read. Editing only the document on screen would make an English half that
 * disagrees, refused at Save with a pointer at a field this surface never
 * showed.
 */
export function setDomainOrder(
  parts: readonly DraftPart[],
  order: number,
): DraftPart[] {
  return editEveryDocument(parts, (document) => {
    document.order = order;
  });
}

/**
 * Switch which opening the runtime reads, on every language.
 *
 * BOTH HALVES, because the service refuses a document carrying the field its
 * `has_opening_question` does not select: the text moves to the selected field
 * and the other is deleted. Moving it rather than blanking it is what makes the
 * control non-destructive — a clinician who flips the switch back finds their
 * text where they left it, and a switch that silently discarded a paragraph
 * would be the worst kind of toggle.
 */
export function setDomainOpeningMode(
  parts: readonly DraftPart[],
  hasOpeningQuestion: boolean,
): DraftPart[] {
  const [next, previous]: [BackgroundOpeningField, BackgroundOpeningField] =
    hasOpeningQuestion
      ? ["opening_prompt", "first_question"]
      : ["first_question", "opening_prompt"];
  return editEveryDocument(parts, (document) => {
    document.has_opening_question = hasOpeningQuestion;
    const carried = document[previous];
    if (typeof carried === "string") {
      document[next] = carried;
    }
    delete document[previous];
  });
}

/**
 * One item's prompt text, in ONE language.
 *
 * Addressed by item ID rather than by position, because a reorder in flight
 * changes the position and not the identity — and because the id is what the
 * conditional rules, safety nets and the tracker itself resolve against.
 */
export function setItemDescription(
  parts: readonly DraftPart[],
  documentId: string,
  itemId: string,
  value: string,
): DraftPart[] {
  return editDocument(parts, documentId, (document) => {
    const item = findItem(document, itemId);
    if (item) {
      item.description = value;
    }
  });
}

function itemsOf(document: JsonRecord): JsonRecord[] | null {
  return Array.isArray(document.items)
    ? (document.items.filter(isRecord) as JsonRecord[])
    : null;
}

function findItem(document: JsonRecord, itemId: string): JsonRecord | null {
  return itemsOf(document)?.find((item) => item.id === itemId) ?? null;
}

/**
 * Move one item to an absolute position, in every language.
 *
 * The whole `items` array is replaced with a REORDERED array of the SAME
 * objects — never rebuilt entries — so each item keeps every field it held,
 * including the ones this surface does not render.
 */
export function moveItem(
  parts: readonly DraftPart[],
  itemId: string,
  to: number,
): DraftPart[] {
  return editEveryDocument(parts, (document) => {
    if (!Array.isArray(document.items)) {
      return;
    }
    const items = [...document.items];
    const from = items.findIndex(
      (item) => isRecord(item) && item.id === itemId,
    );
    if (from === -1 || to < 0 || to >= items.length) {
      return;
    }
    const [moved] = items.splice(from, 1);
    items.splice(to, 0, moved);
    document.items = items;
  });
}

/**
 * Remove one item, in every language.
 *
 * The RULES THAT NAMED IT ARE LEFT ALONE, deliberately. A conditional trigger
 * or safety net referencing a removed item is exactly what
 * `background_domains._validate_references` refuses, with a pointer at the rule
 * — so a clinician is told which rule to fix rather than having it silently
 * rewritten underneath them by a client that cannot see #913's editor. Removing
 * the reference here would be this module inventing a clinical decision.
 */
export function removeItem(
  parts: readonly DraftPart[],
  itemId: string,
): DraftPart[] {
  return editEveryDocument(parts, (document) => {
    if (!Array.isArray(document.items)) {
      return;
    }
    document.items = document.items.filter(
      (item) => !(isRecord(item) && item.id === itemId),
    );
  });
}

/**
 * Append one new item, in every language, at the SAME position.
 *
 * `type: "required"` because a `conditional` item without a rule to activate it
 * never runs, and #913 owns the rule editor: a surface that cannot author the
 * activation must not create the item that needs one. `description` starts
 * empty and the service refuses a blank one, which is the intended path — the
 * clinician types the prompt they came to write, and an untouched new row
 * cannot be published as clinical content.
 */
export function addItem(
  parts: readonly DraftPart[],
  itemId: string,
): DraftPart[] {
  return editEveryDocument(parts, (document) => {
    if (!Array.isArray(document.items)) {
      return;
    }
    document.items = [
      ...document.items,
      { id: itemId, type: "required", description: "" },
    ];
  });
}

/**
 * ## THE CONDITIONAL AUTHORING #913 ADDS, AND WHERE EACH FIELD IS WRITTEN
 *
 * The pinned engine merges a domain's language documents anchored on `he`
 * (`init._merge_lang_configs`): `out = dict(he_cfg)` takes the STRUCTURE, and
 * only `display_name`, `opening_prompt`, `first_question` and each item's
 * `description` are replaced from the other language. `safety_net_triggers` is
 * the one exception — the ACTIVE language's entry wins, falling back to the
 * other when a name is absent there.
 *
 * That split, not a preference, decides which of the two write paths below each
 * field takes:
 *
 *  * **Every document** (`editEveryDocument`, like `order` and the opening
 *    mode): `type`, `patient_info_field`, `critical_gate`, `binary_gate`. They
 *    are typed behaviour with no prose in them, the engine reads the anchor's,
 *    and a divergent copy in another language is a value nothing will ever read.
 *  * **The ANCHOR document** (`editAnchorDocument`): `extraction_note`,
 *    `trigger`, `quality_examples` and `conditional_trigger_rules`.
 *
 *    An earlier version of this module wrote them into the document being
 *    EDITED, reasoning that they are prose and that base canon's `he` and `en`
 *    copies legitimately differ on them. Both halves are true and the
 *    conclusion was wrong: `_merge_lang_configs` builds a domain from
 *    `dict(he_cfg)` and then replaces ONLY `display_name`, `opening_prompt`,
 *    `first_question` and each item's `description`. Every other item field and
 *    the whole rule list are the ANCHOR's, so a non-anchor copy of them is
 *    content the runtime never reads — and a control writing there published
 *    successfully and changed nothing. Refusing to overwrite dead content is
 *    not worth a control that silently does nothing.
 *
 *  * **One named document** (`editDocument`): `description`, and a safety net's
 *    own fields. The first is per-language by construction. The second is the
 *    one collection `_merge_lang_configs` really does select by language — the
 *    ACTIVE conversation language's table wins, falling back to the other
 *    language for a name it does not carry — so an entry is edited AT THE
 *    DOCUMENT IT LIVES IN, which `effectiveSafetyNets` reports and which for an
 *    inherited net is another language's. Copying it into the anchor instead
 *    would mint a second entry that then shadows the one the runtime reads.
 *
 *    Its two STRUCTURAL operations are not per-document at all: `addSafetyNet`
 *    writes into the anchor so a new name is primary rather than a fallback, and
 *    `removeSafetyNet` strips the name from every language, because removing one
 *    copy reveals the other rather than deleting the net.
 *
 * ## AN EMPTY LIST IS A REMOVED KEY
 *
 * Every list the schema admits INSIDE an item, a rule or a safety net is bounded
 * at `min_length=1` when present — `quality_examples`, `value_contains`,
 * `value_not_contains`, `trigger_items`, `deny_items`, `gate_statuses`,
 * `keywords`, `exclude_keywords`, `target_items`. So `[]` is not "the empty
 * version of this field", it is a document the service refuses. Emptying one
 * therefore DELETES its key, which is the same document the source writes when
 * it simply does not use the field.
 *
 * THE TWO DOCUMENT-LEVEL COLLECTIONS ARE THE EXCEPTION, and it is the ticket's
 * own rule rather than a nicety: `conditional_trigger_rules: []` and
 * `safety_net_triggers: {}` are both valid, and the S7 contract requires
 * preserving "valid empty collections". An earlier version deleted them for one
 * uniform spelling, arguing the engine cannot tell an absent list from an empty
 * one — true, and beside the point: the document is what a clinician publishes
 * and what an equivalence run compares, so removing a key the author wrote is
 * this module editing content nobody asked it to. Removing the last entry
 * therefore leaves the collection PRESENT and empty, and a collection that was
 * never there stays absent.
 *
 * Clearing an optional STRING is the same statement — `extraction_note: ""` is
 * refused as blank, and "this item has no extraction note" is the absent key.
 */

/** One item's typed enum: required, or activated by a rule. */
export function setItemType(
  parts: readonly DraftPart[],
  itemId: string,
  type: string,
): DraftPart[] {
  return editEveryDocument(parts, (document) => {
    const item = findItem(document, itemId);
    if (item) {
      item.type = type;
    }
  });
}

/** One item's gate, across every language. `null` removes the key. */
export function setItemFlag(
  parts: readonly DraftPart[],
  itemId: string,
  field: "critical_gate" | "binary_gate",
  value: boolean | null,
): DraftPart[] {
  return editEveryDocument(parts, (document) => {
    const item = findItem(document, itemId);
    if (item) {
      assign(item, field, value);
    }
  });
}

/** The registration key that pre-populates one item, across every language. */
export function setItemPatientInfoField(
  parts: readonly DraftPart[],
  itemId: string,
  value: string,
): DraftPart[] {
  return editEveryDocument(parts, (document) => {
    const item = findItem(document, itemId);
    if (item) {
      assign(item, "patient_info_field", value === "" ? null : value);
    }
  });
}

/** One item's prose, in ONE language. `""` removes the key. */
export function setItemProse(
  parts: readonly DraftPart[],
  itemId: string,
  field: "extraction_note" | "trigger",
  value: string,
): DraftPart[] {
  return editAnchorDocument(parts, (document) => {
    const item = findItem(document, itemId);
    if (item) {
      assign(item, field, value === "" ? null : value);
    }
  });
}

/** One quality example's field, in ONE language. */
export function setQualityExample(
  parts: readonly DraftPart[],
  itemId: string,
  index: number,
  field: "value" | "reason" | "sufficient",
  value: string | boolean,
): DraftPart[] {
  return editAnchorDocument(parts, (document) => {
    const example = examplesOf(findItem(document, itemId))?.[index];
    if (isRecord(example)) {
      example[field] = value;
    }
  });
}

/**
 * Append one example, in ONE language.
 *
 * `sufficient: false` because that is the half that carries the clinical
 * meaning and the half an author is most often adding — and because a REQUIRED
 * boolean cannot start absent. `value` and `reason` start empty and the service
 * refuses a blank one, which is the intended path: the row is a prompt to write
 * the example, and an untouched one cannot be published.
 */
export function addQualityExample(
  parts: readonly DraftPart[],
  itemId: string,
): DraftPart[] {
  return editAnchorDocument(parts, (document) => {
    const item = findItem(document, itemId);
    if (!item) {
      return;
    }
    const examples = examplesOf(item) ?? [];
    item.quality_examples = [
      ...examples,
      { value: "", sufficient: false, reason: "" },
    ];
  });
}

/** Remove one example, in ONE language. The last one removes the key. */
export function removeQualityExample(
  parts: readonly DraftPart[],
  itemId: string,
  index: number,
): DraftPart[] {
  return editAnchorDocument(parts, (document) => {
    const item = findItem(document, itemId);
    const examples = examplesOf(item);
    if (!item || !examples || index < 0 || index >= examples.length) {
      return;
    }
    assign(
      item,
      "quality_examples",
      listOrNull(examples.filter((_entry, at) => at !== index)),
    );
  });
}

/** Move one example to an absolute position, in ONE language. */
export function moveQualityExample(
  parts: readonly DraftPart[],
  itemId: string,
  index: number,
  to: number,
): DraftPart[] {
  return editAnchorDocument(parts, (document) => {
    const item = findItem(document, itemId);
    const examples = examplesOf(item);
    if (item && examples) {
      item.quality_examples = reorder(examples, index, to);
    }
  });
}

/**
 * Append one deterministic rule, in ONE language.
 *
 * SEEDED WITH A REAL GATE ITEM rather than a blank one: `when` is bounded by an
 * item-id pattern, so an empty string is a document the service refuses before
 * the clinician has done anything, and the control that edits it is a picker
 * over this domain's items — it has no way to show "nothing chosen" that is also
 * a valid document. `captured` is the status the source's own rules key on most.
 */
export function addRule(
  parts: readonly DraftPart[],
  when: string,
  equals: string,
): DraftPart[] {
  return editAnchorDocument(parts, (document) => {
    document.conditional_trigger_rules = [
      ...(rulesOf(document) ?? []),
      { when, equals },
    ];
  });
}

/** Remove one rule, in ONE language. The last one removes the key. */
export function removeRule(
  parts: readonly DraftPart[],
  index: number,
): DraftPart[] {
  return editAnchorDocument(parts, (document) => {
    const rules = rulesOf(document);
    if (!rules || index < 0 || index >= rules.length) {
      return;
    }
    // PRESENT AND EMPTY, not removed: the key is here because the document
    // carries it, and `[]` is a shape the schema admits.
    document.conditional_trigger_rules = rules.filter(
      (_rule, at) => at !== index,
    );
  });
}

/** Move one rule to an absolute position, in ONE language. */
export function moveRule(
  parts: readonly DraftPart[],
  index: number,
  to: number,
): DraftPart[] {
  return editAnchorDocument(parts, (document) => {
    const rules = rulesOf(document);
    if (rules) {
      document.conditional_trigger_rules = reorder(rules, index, to);
    }
  });
}

/** One rule's gate item or status, in ONE language. */
export function setRuleGate(
  parts: readonly DraftPart[],
  index: number,
  field: "when" | "equals",
  value: string,
): DraftPart[] {
  return editAnchorDocument(parts, (document) => {
    const rule = rulesOf(document)?.[index];
    if (isRecord(rule)) {
      rule[field] = value;
    }
  });
}

/** One rule's list field, in ONE language. An empty list removes the key. */
export function setRuleList(
  parts: readonly DraftPart[],
  index: number,
  field:
    | "value_contains"
    | "value_not_contains"
    | "trigger_items"
    | "deny_items",
  values: readonly string[],
): DraftPart[] {
  return editAnchorDocument(parts, (document) => {
    const rule = rulesOf(document)?.[index];
    if (isRecord(rule)) {
      assign(rule, field, listOrNull([...values]));
    }
  });
}

/**
 * Add one named safety net, in ONE language.
 *
 * The name is IDENTITY — `initialize_bg_tracker` namespaces an entry under
 * `<domain>/<name>` — so it is generated here and never edited afterwards, on
 * the same rule that governs item ids. It is generated with the leading
 * underscore the engine reads as "this entry names no item of its own", which
 * is why `targets` is required and seeded: the alternative spelling, a name that
 * IS an item id, says the same thing with one implicit target and stays fully
 * editable wherever a base document already uses it.
 */
export function addSafetyNet(
  parts: readonly DraftPart[],
  name: string,
  gateItem: string,
  targets: readonly string[],
): DraftPart[] {
  return editAnchorDocument(parts, (document) => {
    const nets = netsOf(document) ?? {};
    const entry: JsonRecord = { gate_item: gateItem };
    // `listOrNull`, not `[...targets]`: `target_items` is bounded at
    // `min_length=1` when present, so an empty one is a document the service
    // refuses. The caller's control cannot reach here with none — a domain with
    // no prompts offers no Add — and writing the key anyway would still be this
    // module producing content it knows is invalid.
    assign(entry, "target_items", listOrNull([...targets]));
    document.safety_net_triggers = { ...nets, [name]: entry };
  });
}

/**
 * Remove one named safety net from EVERY stored language, leaving the
 * collection present and empty when it held nothing else.
 *
 * Both halves of that used to be documented the other way round, one line above
 * a body comment that already said otherwise — the one-liner was written for the
 * per-language, delete-the-key version and outlived both.
 */
export function removeSafetyNet(
  parts: readonly DraftPart[],
  name: string,
): DraftPart[] {
  // EVERY DOCUMENT, and that is the engine's model rather than a convenience.
  // `_merge_lang_configs` unions the trigger NAMES across languages and uses
  // the other language's entry for any name the active one does not carry — so
  // deleting one copy does not remove the net, it reveals the other one. A
  // control called "Remove" that revived a net from the language behind it
  // would be the worst kind of button.
  return editEveryDocument(parts, (document) => {
    const nets = netsOf(document);
    if (!nets || !(name in nets)) {
      return;
    }
    document.safety_net_triggers = Object.fromEntries(
      Object.entries(nets).filter(([held]) => held !== name),
    );
  });
}

/** One safety net's gate item, in ONE language. */
export function setSafetyNetGateItem(
  parts: readonly DraftPart[],
  documentId: string,
  name: string,
  value: string,
): DraftPart[] {
  return editDocument(parts, documentId, (document) => {
    const net = netsOf(document)?.[name];
    if (isRecord(net)) {
      net.gate_item = value;
    }
  });
}

/** One safety net's list field, in ONE language. An empty list removes the key. */
export function setSafetyNetList(
  parts: readonly DraftPart[],
  documentId: string,
  name: string,
  field: "gate_statuses" | "keywords" | "exclude_keywords" | "target_items",
  values: readonly string[],
): DraftPart[] {
  return editDocument(parts, documentId, (document) => {
    const net = netsOf(document)?.[name];
    if (isRecord(net)) {
      assign(net, field, listOrNull([...values]));
    }
  });
}

/** Write a value, or DELETE the key when the value is `null`. */
function assign(target: JsonRecord, key: string, value: unknown): void {
  if (value === null) {
    delete target[key];
  } else {
    target[key] = value;
  }
}

/** The list, or `null` when it is empty — the "empty means absent" rule. */
function listOrNull<T>(values: T[]): T[] | null {
  return values.length === 0 ? null : values;
}

/** The same array with one entry moved, or the original when `to` is outside it. */
function reorder<T>(values: readonly T[], from: number, to: number): T[] {
  if (from < 0 || from >= values.length || to < 0 || to >= values.length) {
    return [...values];
  }
  const next = [...values];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved as T);
  return next;
}

/**
 * The stored arrays, UNFILTERED, and that is the correctness of these two.
 *
 * An earlier version returned `…filter(isRecord)`, which is wrong twice over
 * for content this module deliberately reads leniently. `readDomainDocument`
 * projects the RAW array — a non-object entry still occupies a position, and
 * dropping it here would renumber every entry after it — so a filtered array
 * indexed by the position on screen edits the WRONG row as soon as one
 * unreadable entry precedes it. And `moveQualityExample`/`moveRule` assign the
 * result back, so a filtered array would silently delete that entry from a
 * stored document during an unrelated reorder.
 *
 * The entries are therefore checked one at a time, where they are used.
 */
function examplesOf(item: JsonRecord | null): unknown[] | null {
  return item && Array.isArray(item.quality_examples)
    ? item.quality_examples
    : null;
}

function rulesOf(document: JsonRecord): unknown[] | null {
  return Array.isArray(document.conditional_trigger_rules)
    ? document.conditional_trigger_rules
    : null;
}

function netsOf(document: JsonRecord): JsonRecord | null {
  return isRecord(document.safety_net_triggers)
    ? document.safety_net_triggers
    : null;
}

/**
 * `_net_1`, `_net_2`, … — the first name free in EVERY stored language.
 *
 * Across languages although the entry is written into one, because the engine
 * unions the names when it selects an active language's table: reusing a name
 * another language already holds would silently make the two entries one row
 * with a fallback between them.
 */
export function nextSafetyNetName(parts: readonly DraftPart[]): string {
  const used = new Set<string>();
  for (const document of domainDocuments(parts)) {
    for (const net of document.view?.safetyNetTriggers ?? []) {
      used.add(net.name);
    }
  }
  for (let index = used.size + 1; ; index += 1) {
    const candidate = `_net_${index}`;
    if (!used.has(candidate)) {
      return candidate;
    }
  }
}

/**
 * The safety nets the RUNTIME would read for one conversation language.
 *
 * ## WHY A MERGED SET AND NOT ONE DOCUMENT'S TABLE
 *
 * `_merge_lang_configs` does not read a domain's safety nets out of one
 * language. It unions the trigger NAMES across both, takes the active
 * language's entry for a name it carries, and FALLS BACK to the other
 * language's entry for a name it does not:
 *
 *     for trigger_id in set(list(primary.keys()) + list(fallback.keys())):
 *         combined[trigger_id] = dict(primary[trigger_id] if … else fallback[…])
 *
 * A panel that rendered one document's table therefore lied in three separate
 * ways: an English-only net runs for a Hebrew conversation and did not appear;
 * removing the Hebrew copy of a name looked like a deletion and was a REVERSION
 * to the English one; and a domain whose nets all live in the other language
 * read "No safety nets" while one was firing.
 *
 * So this returns what runs, each entry carrying the document it actually lives
 * in — which is the address an edit has to use, because editing an inherited net
 * means editing the copy the runtime is reading, not minting a second one.
 *
 * ORDER is the active language's stored order first, then the names only the
 * other language carries, in theirs. The engine iterates a `set` and has no
 * order of its own; a stable one is what keeps rows from jumping as fields
 * change.
 */
export interface EffectiveSafetyNet {
  net: BackgroundSafetyNetView;
  /** The `DraftPart.id` the entry lives in — the edit address. */
  documentId: string;
  /** That document's language, or `null` for a language-neutral part. */
  language: string | null;
  /**
   * True when `activeLanguage` carries no net of this name and the runtime is
   * reading another language's. Editing it edits that copy.
   */
  inherited: boolean;
  /**
   * The other languages that ALSO carry this name. Their conversations read
   * their own copy, not the one on screen, and a clinician changing a shared
   * net needs to know that before they wonder why nothing moved.
   */
  alsoIn: string[];
}

export function effectiveSafetyNets(
  parts: readonly DraftPart[],
  activeLanguage: string,
): EffectiveSafetyNet[] {
  const documents = domainDocuments(parts).filter(
    (document) => document.view !== null,
  );
  const primary = documents.find(
    (document) => document.language === activeLanguage,
  );
  const others = documents.filter((document) => document !== primary);
  const held = (document: BackgroundDomainDocument) =>
    document.view?.safetyNetTriggers ?? [];

  const alsoIn = (name: string, from: BackgroundDomainDocument) =>
    documents
      .filter(
        (document) =>
          document !== from &&
          held(document).some((candidate) => candidate.name === name),
      )
      .map((document) => document.language ?? "all languages");

  const effective: EffectiveSafetyNet[] = [];
  const seen = new Set<string>();
  for (const net of primary ? held(primary) : []) {
    seen.add(net.name);
    effective.push({
      net,
      documentId: (primary as BackgroundDomainDocument).documentId,
      language: (primary as BackgroundDomainDocument).language,
      inherited: false,
      alsoIn: alsoIn(net.name, primary as BackgroundDomainDocument),
    });
  }
  for (const document of others) {
    for (const net of held(document)) {
      if (seen.has(net.name)) {
        continue;
      }
      seen.add(net.name);
      effective.push({
        net,
        documentId: document.documentId,
        language: document.language,
        // Only when there IS an active-language document to be missing from:
        // a monolingual domain inherits nothing, it simply is the one table.
        inherited: primary !== undefined,
        alsoIn: alsoIn(net.name, document),
      });
    }
  }
  return effective;
}

/**
 * One entry per rule or safety net naming an item its document does not hold.
 *
 * ## WHY THIS IS IN THE BROWSER, AND WHAT IT IS NOT
 *
 * The rule is the service's — `background_domains._validate_references` refuses
 * exactly these documents, with a pointer at the offending reference — and this
 * module restates no other part of that schema. It restates THIS one for the
 * reason `blankDescriptions` gives for the blank-prompt rule, and a sharper one:
 * removing an item is the single edit on this surface that can break a rule the
 * clinician is not looking at, in a language that is not on screen. #859 left
 * those references alone deliberately, so that a clinician is told which rule to
 * fix rather than having it rewritten underneath them — and being told is this
 * function.
 *
 * The server still decides. This turns `content.parts[0].data.
 * conditional_trigger_rules[3].trigger_items[1]` into a sentence naming the rule
 * and the prompt that is gone, BEFORE the request, while the work is still in
 * the draft.
 *
 * EVERY LANGUAGE, not the one on screen: the item list is shared, so deleting a
 * prompt breaks the same reference in every document that carries a rule for it.
 */
export interface ReferenceProblem {
  /** The document's language, or `"all languages"` for a neutral part. */
  language: string;
  /** Which reference is broken, in words a clinician can act on. */
  where: string;
  /** What it names. The empty string when the reference is missing entirely. */
  missingId: string;
}

export function referenceProblems(
  parts: readonly DraftPart[],
): ReferenceProblem[] {
  const problems: ReferenceProblem[] = [];
  for (const document of domainDocuments(parts)) {
    const view = document.view;
    if (view === null) {
      continue;
    }
    const language = document.language ?? "all languages";
    const known = new Set(view.items.map((item) => item.id));
    const report = (where: string, missingId: string) =>
      problems.push({ language, where, missingId });

    view.conditionalTriggerRules?.forEach((rule, index) => {
      const rule_at = `rule ${index + 1}`;
      if (!known.has(rule.when)) {
        report(`${rule_at}, its gate prompt`, rule.when);
      }
      for (const [field, label] of [
        ["triggerItems", "activates"],
        ["denyItems", "skips"],
      ] as const) {
        for (const target of rule[field] ?? []) {
          if (!known.has(target)) {
            report(`${rule_at}, ${label}`, target);
          }
        }
      }
    });

    for (const net of view.safetyNetTriggers ?? []) {
      const net_at = `safety net ${net.name}`;
      if (!known.has(net.gateItem)) {
        report(`${net_at}, its gate prompt`, net.gateItem);
      }
      if (net.targetItems === null) {
        // The engine's own default, restated because it is the one place an
        // ABSENT list is an error rather than an omission: a `_`-prefixed name
        // names no item, and a bare name targets the item it spells.
        if (net.name.startsWith("_") || !known.has(net.name)) {
          report(`${net_at}, its targets`, "");
        }
        continue;
      }
      for (const target of net.targetItems) {
        if (!known.has(target)) {
          report(`${net_at}, targets`, target);
        }
      }
    }
  }
  return problems;
}

/**
 * One entry per prompt that is still BLANK in some stored language.
 *
 * ## WHY THIS IS IN THE BROWSER AT ALL
 *
 * The rule is the service's — `background_domains.DocumentText` refuses a
 * `description` with no visible character, per language — and this module
 * deliberately restates none of its schema. What it restates is this one rule,
 * for the same reason `assetContent.findPartProblems` restates the non-empty
 * prompt: adding a prompt writes the new item into EVERY language (structure is
 * shared) while the editor shows ONE, so an author writing Hebrew cannot see
 * that the English document now holds a blank required field. Without this the
 * only feedback is a 422 whose pointer — `content.parts[0].data.items[6]` —
 * names a document that is not on screen.
 *
 * The server still decides. This turns an unattributable refusal into a
 * sentence naming the prompt and the language, before the request.
 *
 * BLANK, NOT ABSENT: an item a language does not carry at all is not reported.
 * Languages legitimately differ — base canon's own `he` and `en` documents
 * differ on extraction notes, triggers and quality examples — and a rule about
 * ITEM SETS is not one this module has the authority to invent.
 */
export interface BlankDescription {
  itemId: string;
  /** The languages whose stored document holds it blank, in part order. */
  languages: string[];
}

export function blankDescriptions(
  parts: readonly DraftPart[],
): BlankDescription[] {
  const byItem = new Map<string, string[]>();
  for (const document of domainDocuments(parts)) {
    for (const item of document.view?.items ?? []) {
      if (item.description.trim() !== "") {
        continue;
      }
      const languages = byItem.get(item.id) ?? [];
      languages.push(document.language ?? "all languages");
      byItem.set(item.id, languages);
    }
  }
  return [...byItem].map(([itemId, languages]) => ({ itemId, languages }));
}

/** The item ids in use across every language, so a new one cannot collide. */
export function itemIdsInUse(parts: readonly DraftPart[]): Set<string> {
  const ids = new Set<string>();
  for (const document of domainDocuments(parts)) {
    for (const item of document.view?.items ?? []) {
      ids.add(item.id);
    }
  }
  return ids;
}

/**
 * `step_1`, `step_2`, … — the first that is free.
 *
 * A SUGGESTION now, not the id (#977). #859 generated this one BECAUSE an item
 * id is the stable key the tracker, the conditional rules and the safety nets
 * resolve against and the ratified contract's rule is that clinical ids are not
 * renamed — so putting the permanent identity of a runtime record behind a
 * free-text field looked like the larger risk, and the display name a clinician
 * cares about is the prompt text itself.
 *
 * That reasoning holds for RENAMING and there is still no rename control. It
 * does not hold for CREATION, and #872 is where the difference shows: MOD's six
 * source domains hold 35 item ids, 15 of which no base document supplies, and
 * every one of those is named by a `conditional_trigger_rules` reference. An id
 * that cannot be typed is an interview that cannot be rebuilt — so the id is
 * now the author's to choose, and this is what the box starts with when they
 * have no opinion.
 */
export function nextItemId(parts: readonly DraftPart[]): string {
  const used = itemIdsInUse(parts);
  for (let index = used.size + 1; ; index += 1) {
    const candidate = `step_${index}`;
    if (!used.has(candidate)) {
      return candidate;
    }
  }
}

/**
 * Why a typed id may not be created here, or `null` when it may be (#977).
 *
 * A REASON CODE, never a sentence: the same split `backgroundVocabulary.ts`
 * documents for the tracker statuses. What is refused is a fact about the
 * document and the service's grammar; how it READS is the surface's decision,
 * and putting product copy in this module would make a wording change a change
 * to the module every BG edit path goes through.
 *
 * `unreadable_policy` is the case an "is it valid" boolean cannot express, and
 * the one #859's review round 1 was about: with no pattern in hand every
 * candidate would "pass", and a surface would mint a permanent clinical id
 * against a grammar it never read. Distinguishing "we know this is wrong" from
 * "we do not know" is what lets the surface say which. The CALLER decides when
 * it does not know — `BackgroundDomainPage` passes `null` unless the vocabulary
 * query's LAST read succeeded, so a refused refresh over a cached response is
 * "we do not know" and not "we knew once".
 *
 * DUPLICATES ARE CHECKED ACROSS EVERY LANGUAGE, because `addItem` writes the
 * new item into every stored document: an id free in the Hebrew half and taken
 * in the English one is a duplicate the moment it is added, refused by
 * `background_domains` with a pointer at a document the author never opened.
 */
export type NewItemIdRefusal = "blank" | "unreadable_policy" | "shape" | "duplicate";

export function newItemIdRefusal(
  parts: readonly DraftPart[],
  itemId: string,
  /** The service's own unanchored `ItemId` grammar, or `null` while unread. */
  pattern: string | null,
): NewItemIdRefusal | null {
  // VISUALLY blank, not zero-length. A box holding only spaces LOOKS empty, and
  // "you have not named this prompt" is the fact — telling that author their id
  // is the wrong SHAPE describes a value they cannot see. It is the same cut
  // `background_domains._not_blank` makes for required text, one control
  // earlier.
  //
  // Nothing is normalized by this: `trim()` decides the REASON and never the
  // value, the id created is exactly what was typed, and an id with spaces
  // anywhere in it is refused by the grammar below regardless.
  if (itemId.trim() === "") {
    return "blank";
  }
  if (pattern === null) {
    return "unreadable_policy";
  }
  // ANCHORED HERE, in this dialect, from the unanchored rule the service
  // publishes — `placement.SEGMENT_PATTERN`'s contract, and the reason no
  // character class is spelled in this file. JavaScript's `$` anchors at
  // end-of-input, which is what pydantic's engine does, so the two agree about
  // a trailing newline (`test_background_domains.py` pins both halves).
  if (!new RegExp(`^(?:${pattern})$`).test(itemId)) {
    return "shape";
  }
  return itemIdsInUse(parts).has(itemId) ? "duplicate" : null;
}

/**
 * The whole domain identity of a set, for a catalog row that has only content.
 *
 * Returns the anchor document's own `domain`/`order`/item count, which is what
 * a clinician scans a catalog by — never the asset NAME, which is editor
 * metadata, and never a value inferred from the placement coordinate, which a
 * seeded row does not carry.
 */
export interface BackgroundDomainSummary {
  domain: string;
  displayName: string;
  order: number | null;
  itemCount: number;
}

export function domainSummary(
  parts: readonly DraftPart[],
): BackgroundDomainSummary | null {
  return summaryOf(anchorDocument(domainDocuments(parts))?.view ?? null);
}

/**
 * The same summary, read from a STORED version's content.
 *
 * The read-only surfaces — the catalog row and the Project row's interview
 * position — hold `LibraryAssetVersionDetail["content"]`, not a draft. Routing
 * them through `toDraft` would mint a `DraftPart.id` per part per render from
 * the module-scoped counter `assetContent` keeps for React keys, which is
 * identity for content nobody is editing. So this reads the stored shape
 * directly and the draft projection stays with the editor that needs it.
 */
export function storedDomainSummary(
  content: unknown,
): BackgroundDomainSummary | null {
  if (!isRecord(content) || !Array.isArray(content.parts)) {
    return null;
  }
  const views: { language: string | null; view: BackgroundDomainView }[] = [];
  for (const part of content.parts) {
    if (!isRecord(part) || typeof part.key !== "string") {
      continue;
    }
    const [language, role] = splitPartKey(part.key);
    if (role !== BG_DOMAINS_ROLE) {
      continue;
    }
    const view = readDomainDocument(JSON.stringify(part.data));
    if (view) {
      views.push({ language, view });
    }
  }
  const anchored = views.find(
    (entry) => entry.language === BG_STRUCTURE_LANGUAGE,
  );
  const chosen = anchored ?? (views.length === 1 ? views[0] : undefined);
  return summaryOf(chosen?.view ?? null);
}

function summaryOf(
  view: BackgroundDomainView | null,
): BackgroundDomainSummary | null {
  if (!view) {
    return null;
  }
  return {
    domain: view.domain,
    displayName: view.displayName,
    order: view.order,
    itemCount: view.items.length,
  };
}
